import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Package, ClipboardList, Truck, BarChart3, Smartphone, Printer, FileText, Trash2, PlusCircle, Users, ArrowRightLeft, AlertTriangle, Search, Boxes, Hourglass } from 'lucide-react';
import { Button } from '@/components/ui/button';
import apiClient from '@/services/apiClient';
import { filterOperations } from '@/utils/operationAccess';

const SFGWMSDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    sfgBundles: 0,
    pendingPickSlips: 0,
    pickedToday: 0,
    putAwayToday: 0
  });

  const fetchStats = useCallback(async () => {
    try {
      const sfgResponse = await apiClient.get('/wms/sfg/inventory?status=STORED');
      setStats({
        sfgBundles: sfgResponse.data.total || 0,
        pendingPickSlips: 0,
        pickedToday: 0,
        putAwayToday: 0
      });
    } catch (error) {
      console.error('Error fetching SFG WMS stats:', error);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const modules = [
    {
      id: 'create-pick-slip',
      title: 'Create Pick Slip',
      description: 'Manually create SFG pick slip with cascading filters',
      icon: PlusCircle,
      route: '/wms/sfg/create-pick-slip',
      color: 'bg-green-500',
      roles: ['Admin', 'Dispatch Supervisor', 'Dispatch Incharge']
    },
    {
      id: 'sfg-putaway',
      title: 'SFG Put-Away',
      description: 'Scan and put away bundles to SFG storage',
      icon: Package,
      route: '/wms/sfg-putaway',
      color: 'bg-purple-500'
    },
    {
      id: 'create-pack',
      title: 'Create Pack',
      description: 'Group finished pieces into a 5-pack for one-scan store & pick',
      icon: Boxes,
      route: '/wms/fg/create-pack',
      color: 'bg-fuchsia-600'
    },
    {
      id: 'main-label-printing',
      title: 'Main Label Printing',
      description: 'Print labels for picked bundles (stickering)',
      icon: Printer,
      route: '/wms/sfg/main-label-printing',
      color: 'bg-amber-500'
    },
    {
      id: 'pick-slips',
      title: 'Pick Slips',
      description: 'View and manage replenishment pick slips',
      icon: ClipboardList,
      route: '/wms/pick-slips',
      color: 'bg-cyan-500'
    },
    {
      id: 'picker-guidance',
      title: 'Picker Guidance',
      description: 'Step-by-step picking workflow',
      icon: Truck,
      route: '/wms/picker-guidance',
      color: 'bg-rose-500'
    },
    {
      id: 'replenishment',
      title: 'Replenishment Dashboard',
      description: 'Monitor stock levels and trigger replenishment',
      icon: BarChart3,
      route: '/wms/replenishment',
      color: 'bg-indigo-500'
    },
    {
      id: 'sfg-inventory-report',
      title: 'SFG Inventory Report',
      description: 'View all bundles stored in SFG bins',
      icon: FileText,
      route: '/wms/sfg/inventory-report',
      color: 'bg-teal-500'
    },
    {
      id: 'sfg-ageing-report',
      title: 'Stock Ageing Report',
      description: 'SFG stock by age range (0-30 … >365 days, editable)',
      icon: Hourglass,
      route: '/reports/storage-ageing?tab=sfg',
      color: 'bg-amber-600'
    },
    {
      id: 'sfg-put-away-entries',
      title: 'Put Away Entries (Admin)',
      description: 'View and delete SFG put away entries',
      icon: Trash2,
      route: '/wms/sfg/put-away-entries',
      color: 'bg-red-500'
    },
    {
      id: 'in-wip-stock-report',
      title: 'In-WIP Stock Report',
      description: 'Bundles with piece processing staff',
      icon: Users,
      route: '/wms/sfg/in-wip-stock-report',
      color: 'bg-orange-500'
    },
    {
      id: 'in-wip-bundle-status',
      title: 'IN-WIP Bundle Status',
      description: 'Per-bundle USN accounting: FG, combo, damage, returned, floor',
      icon: BarChart3,
      route: '/wms/sfg/in-wip-bundle-status',
      color: 'bg-slate-600'
    },
    {
      id: 'sfg-floor-clearance',
      title: 'Floor Clearance',
      description: 'Re-bundle IN_WIP pieces with a new sticker and return to SFG',
      icon: Boxes,
      route: '/wms/sfg/floor-clearance',
      color: 'bg-teal-600'
    },
    {
      id: 'sfg-design-transfer',
      title: 'SFG Design Transfer',
      description: 'Transfer entire bundle to a new design',
      icon: ArrowRightLeft,
      route: '/wms/sfg/design-transfer',
      color: 'bg-purple-500'
    },
    {
      id: 'sfg-investigation-queue',
      title: 'Investigation Queue',
      description: 'Review not found, damaged, or misplaced bundles',
      icon: AlertTriangle,
      route: '/wms/sfg/investigation-queue',
      color: 'bg-amber-500'
    },
    {
      id: 'usn-lookup',
      title: 'USN Lookup',
      description: 'Find put-away slip by scanning USN',
      icon: Search,
      route: '/wms/sfg/usn-lookup',
      color: 'bg-cyan-500'
    },
    {
      id: 'sfg-bin-transfer',
      title: 'Bin to Bin Transfer',
      description: 'Transfer bundles between SFG bins',
      icon: ArrowRightLeft,
      route: '/wms/sfg/bin-transfer',
      color: 'bg-indigo-500'
    },
    {
      id: 'wip-damage-clearance',
      title: 'WIP Damage Clearance',
      description: 'Scan damaged pieces to clear from WIP',
      icon: Trash2,
      route: '/wms/damage/clearance',
      color: 'bg-red-500',
      roles: ['Admin', 'Dispatch Supervisor', 'Dispatch Incharge']
    }
  ];

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center">
            <Button
              variant="ghost"
              onClick={() => navigate('/wms')}
              className="text-[#F9FAFB] hover:bg-white/10 mr-4"
              data-testid="sfg-wms-back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <Package className="w-8 h-8" />
              <div>
                <h1 className="text-xl font-bold font-display">SFG WMS</h1>
                <p className="text-sm text-[#E8DCC8]">Semi-Finished Goods Warehouse</p>
              </div>
            </div>
          </div>
          <Button
            onClick={() => navigate('/wms/mobile')}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="sfg-wms-mobile-view-btn"
          >
            <Smartphone className="w-4 h-4 mr-2" />
            Mobile View
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg border border-[#E5DFD0] p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#5A5F6E]">SFG Bundles in Storage</p>
                <p className="text-3xl font-bold text-[#1A1F2E] mt-1">{stats.sfgBundles}</p>
              </div>
              <Package className="w-10 h-10 text-purple-500" />
            </div>
          </div>
          <div className="bg-white rounded-lg border border-[#E5DFD0] p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#5A5F6E]">Pending Pick Slips</p>
                <p className="text-3xl font-bold text-[#1A1F2E] mt-1">{stats.pendingPickSlips}</p>
              </div>
              <ClipboardList className="w-10 h-10 text-cyan-500" />
            </div>
          </div>
          <div className="bg-white rounded-lg border border-[#E5DFD0] p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#5A5F6E]">Picked Today</p>
                <p className="text-3xl font-bold text-[#1A1F2E] mt-1">{stats.pickedToday}</p>
              </div>
              <Truck className="w-10 h-10 text-rose-500" />
            </div>
          </div>
          <div className="bg-white rounded-lg border border-[#E5DFD0] p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#5A5F6E]">Put Away Today</p>
                <p className="text-3xl font-bold text-[#1A1F2E] mt-1">{stats.putAwayToday}</p>
              </div>
              <Package className="w-10 h-10 text-indigo-500" />
            </div>
          </div>
        </div>

        {/* Module Cards */}
        <div>
          <h2 className="text-2xl font-bold text-[#1A1F2E] mb-6 font-display">SFG Operations</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {filterOperations('sfg_wms', modules).map((module) => {
              const Icon = module.icon;
              return (
                <div
                  key={module.id}
                  onClick={() => navigate(module.route)}
                  className="bg-white rounded-lg border-2 border-[#E5DFD0] hover:border-[#2D5F4F] shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer overflow-hidden"
                  data-testid={`sfg-wms-module-${module.id}`}
                >
                  <div className={`${module.color} p-4`}>
                    <Icon className="w-10 h-10 text-white" />
                  </div>
                  <div className="p-4">
                    <h3 className="text-lg font-semibold text-[#1A1F2E] mb-1 font-display">
                      {module.title}
                    </h3>
                    <p className="text-sm text-[#5A5F6E]">{module.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </main>
    </div>
  );
};

export default SFGWMSDashboard;
